import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from '@backstage/core-components';
import ExtensionIcon from '@material-ui/icons/Extension';
import { zambianColors } from '../../theme/zambianTheme';

const useStyles = makeStyles(theme => ({
  empty: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  avatar: {
    backgroundColor: zambianColors.greenDark,
    color: '#FFFFFF',
  },
  link: {
    fontWeight: 600,
  },
  meta: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
  },
}));

type Props = {
  limit?: number;
  /** When true, suppresses the empty-state block (used on public landing). */
  hideWhenEmpty?: boolean;
};

const formatRelative = (iso?: string): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const minutes = Math.round(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
};

const entityRoute = (e: Entity): string => {
  const ns = e.metadata.namespace || 'default';
  return `/catalog/${ns}/${e.kind.toLowerCase()}/${e.metadata.name}`;
};

const updatedAt = (e: Entity): string | undefined => {
  const anno = e.metadata.annotations || {};
  return (
    (e.metadata as { lastUpdated?: string }).lastUpdated ||
    anno['backstage.io/updated-at']
  );
};

export const RecentActivityFeed = ({ limit = 5, hideWhenEmpty }: Props) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [entities, setEntities] = useState<Entity[] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: [{ kind: 'Component' }, { kind: 'API' }, { kind: 'System' }],
        fields: [
          'kind',
          'metadata.name',
          'metadata.namespace',
          'metadata.title',
          'metadata.description',
          'metadata.annotations',
          'spec.type',
          'spec.owner',
        ],
      })
      .then(res => {
        if (cancelled) return;
        setEntities(res.items.slice(0, limit));
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, limit]);

  if (error) {
    if (hideWhenEmpty) return null;
    return (
      <Box className={classes.empty}>
        <Typography variant="body2">
          Couldn't load recent catalog activity. Try refreshing the page.
        </Typography>
      </Box>
    );
  }

  if (entities && entities.length === 0) {
    if (hideWhenEmpty) return null;
    return (
      <Box className={classes.empty}>
        <Typography variant="body2">
          No recent catalog activity to show yet.
        </Typography>
      </Box>
    );
  }

  if (!entities) {
    return (
      <Box className={classes.empty}>
        <Typography variant="body2">Loading recent activity…</Typography>
      </Box>
    );
  }

  return (
    <List dense>
      {entities.map(e => {
        const title = e.metadata.title || e.metadata.name;
        const owner =
          (e.spec as { owner?: string } | undefined)?.owner ?? 'unowned';
        const type =
          (e.spec as { type?: string } | undefined)?.type ??
          e.kind.toLowerCase();
        const when = formatRelative(updatedAt(e));
        return (
          <ListItem
            key={`${e.kind}:${e.metadata.namespace}/${e.metadata.name}`}
            disableGutters
          >
            <ListItemAvatar>
              <Avatar className={classes.avatar}>
                <ExtensionIcon />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Link to={entityRoute(e)} className={classes.link}>
                  {title}
                </Link>
              }
              secondary={
                <span className={classes.meta}>
                  {e.kind} · {type} · {owner}
                  {when ? ` · ${when}` : ''}
                </span>
              }
            />
          </ListItem>
        );
      })}
    </List>
  );
};
